import React from 'react';
import {observable, action, computed, makeObservable} from 'mobx';
import {observer} from 'mobx-react';
import s from '../../s';
import {InsigniaCustomization} from '../../i';
import {InsigniaList} from './components/insignia-list';
import * as emitter from '../../../global/emitter';
import {capitalize, Portal} from '../../../util';
import {InsigniaPosition} from './components/insignia-position';
import {screenResizeStore} from '../../../global/store/screen-resize-store';
import {isProductCompactViewport, isProductDesktopViewport} from '../viewport/viewport';
import {initInsigniaSelection, insigniaIconUi, insigniaLinoChanged, insigniaListShown, insigniaPositionShown, insigniaRemovable, insigniaView, removeInsignia, resetInsigniaView, restoreOriginalInsignia, selectInsignia, setInsigniaCategory, visibleInsignias} from '../../selection/insignia';
import {disclosurePanelClass} from '../disclosure/panel-class';

export const getTitle = (title = s.ui.title_insignia, color = s.ps.insignia_dyf_color) => {
    const hasColorInTitle = title?.toLowerCase().includes(color?.toLowerCase());

    if (s.ps.is_caf || !color || hasColorInTitle) {
        return title;
    } else {
        return `${title} ${capitalize(color)}`;
    }
};

@observer
export default class Insignia extends React.Component {
    @observable init_ins: boolean = false;
    @observable orig_ins_name: string = '';
    @observable collapsed: boolean = true;
    @observable collapsed_number: number = 4;
    @observable panelOpen: boolean = false;
    icon_ui: boolean = false;

    constructor(props) {
        super(props);
        this.initInsignia = this.initInsignia.bind(this);
        this.orig = this.orig.bind(this);
        this.save = this.save.bind(this);
        this.collapseToggle = this.collapseToggle.bind(this);
        this.changeCategory = this.changeCategory.bind(this);
        this.ins_remove = this.ins_remove.bind(this);
        this.LinoChanged = this.LinoChanged.bind(this);
        // the seal category filter and the CAF dropdown's visibility live with the shared selection (selection/insignia.ts)
        resetInsigniaView();

        this.icon_ui = insigniaIconUi();

        // console.log(toJS(s.prod.insignias));

        makeObservable(this);
    }

    componentDidMount() {
        emitter.eventEmitter.subscribe(emitter.EventType.AB, 'lino_changed', this.LinoChanged);
    }

    componentWillUnmount() {
        emitter.eventEmitter.unsubscribe(emitter.EventType.AB, 'lino_changed');
    }

    @action
    LinoChanged = async () => {
        insigniaLinoChanged();
    };

    @action
    initInsignia = async () => {
        this.orig_ins_name = JSON.parse(JSON.stringify(s.prod.orig_pcs.Base_InsigniaName));
        this.init_ins = true;

        initInsigniaSelection(this.icon_ui, this.orig_ins_name);
    };

    @action
    orig = async () => {
        restoreOriginalInsignia(this.orig_ins_name);
    };

    @action
    save = async (d_i: number) => {
        selectInsignia(d_i);
    };

    @action
    collapseToggle = async () => {
        this.collapsed = !this.collapsed;
        this.collapsed_number = this.collapsed ? 4 : 1000;
    };

    @action
    changeCategory = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        setInsigniaCategory(parseInt(e.target.value));
    };

    @action
    ins_remove = async (e: any) => {
        removeInsignia();
    };

    @action
    togglePanel = () => {
        if (!this.canCollapse) return;
        this.panelOpen = !this.panelOpen;
    };

    @computed get canCollapse(): boolean {
        return s.ps.is_caf || window.innerWidth < 960;
    }

    render() {
        if (!insigniaListShown()) {
            return;
        }
        if (!this.init_ins) {
            this.initInsignia();
        }

        const viewportWidth = screenResizeStore.width;
        const showInsigniaPosition = insigniaPositionShown();

        // the seals the list shows (selection/insignia.ts)
        const insigniaFilteredList = () => visibleInsignias();

        // console.log(toJS(insigniaFilteredList()));
        // console.log(toJS(s.prod));

        // console.log('s.ps.ins_gold_allowed', s.ps.ins_gold_allowed);
        // console.log('s.ps.ins_silver_allowed', s.ps.ins_silver_allowed);
        // console.log('s.ps.ins_black_allowed', s.ps.ins_black_allowed);

        // console.log('s.ps.ins_black_allowed', toJS(s.insigniaChoices.options));

        // console.log('s.insigniaChoices.options.length', s.insigniaChoices.options.length);
        // console.log('insigniaFilteredList.length', insigniaFilteredList.length);

        return (
            <div
                className="gray-box ins-container"
                style={{padding: 0}}
            >
                <div className={this.canCollapse ? '' : ''}>
                    <div style={{padding: '0 12px'}}>
                        <div
                            style={{alignItems: 'center', justifyContent: 'space-between'}}
                            role={this.canCollapse ? 'button' : undefined}
                            tabIndex={this.canCollapse ? 0 : undefined}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                }
                            }}
                            className="d-none d-xl-flex toggle-bs"
                            onClick={this.togglePanel}
                            aria-expanded={this.canCollapse ? this.panelOpen : undefined}
                            aria-controls={this.canCollapse ? 'insignia-inner' : undefined}
                        >
                            <div className="cust-desktop-head">
                                <span>Insignia: </span>
                                <span>{getTitle()}</span>
                            </div>

                            {showInsigniaPosition && isProductDesktopViewport(viewportWidth) && <InsigniaPosition />}
                        </div>

                        <div
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.currentTarget.click();
                                }
                            }}
                            className="d-block d-xl-none toggle-bs"
                            onClick={this.togglePanel}
                            aria-expanded={this.canCollapse ? this.panelOpen : undefined}
                            aria-controls={this.canCollapse ? 'insignia-inner' : undefined}
                        >
                            <div className="cust-flex-head">
                                <div>
                                    <div
                                        className="header-std flexer cust-flex2"
                                        style={{paddingRight: '0.25rem'}}
                                    >
                                        Insignia
                                    </div>
                                    <div className="cust-flex2">{getTitle()}</div>
                                </div>
                                <div>
                                    {this.canCollapse &&
                                        this.collapsed &&
                                        s.prod.insignias
                                            .filter(m => m.id === s.ps.insignia_id)
                                            .map((m: InsigniaCustomization, i: number) => (
                                                <img
                                                    key={i}
                                                    src={`https://${Portal?.CDNDomain}/images/entities/insignia/v2/${m.name.toLowerCase()}_90.png`}
                                                    alt={m.title}
                                                    style={{
                                                        backgroundColor: `#${s.ui.color_mat1}`,
                                                        height: 48,
                                                        width: 'auto',
                                                    }}
                                                />
                                            ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        id="insignia-inner"
                        className={disclosurePanelClass(this.canCollapse, this.panelOpen)}
                        style={{padding: '0 12px'}}
                    >
                        {showInsigniaPosition && isProductCompactViewport(viewportWidth) && <InsigniaPosition />}

                        {(() => {
                            const ins = s.insigniaChoices.options;
                            const excludeMilitary = Portal?.PortalID === 2;
                            const optionCount =
                                (ins.filter(m => !m.isCAF).length > 0 && ins.length > 3 ? 1 : 0) +
                                (ins.filter(m => m.isCAF).length > 0 ? 1 : 0) +
                                (ins.filter(m => m.fpc_id === 21).length > 0 ? 1 : 0) +
                                (ins.filter(m => m.fpc_id === 22).length > 0 ? 1 : 0) +
                                (ins.filter(m => m.fpc_id === 23).length > 0 ? 1 : 0) +
                                (ins.filter(m => m.fpc_id === 24).length > 0 && !excludeMilitary ? 1 : 0) +
                                (ins.filter(m => m.fpc_id === 25).length > 0 ? 1 : 0) +
                                (ins.filter(m => m.fpc_id === 26).length > 0 ? 1 : 0) +
                                (ins.filter(m => m.fpc_id === 27).length > 0 ? 1 : 0) +
                                (ins.filter(m => m.fpc_id === 29).length > 0 ? 1 : 0) +
                                (ins.filter(m => m.fpc_id === 40).length > 0 ? 1 : 0);

                            return s.prod.ProductID !== 75593 && s.prod.ProductID !== 75595 && s.prod.ProductID !== 75591 && s.prod.ProductID !== 75589 && insigniaView.show_dropdown && optionCount > 1 ? (
                                <div
                                    className="form-floating"
                                    style={{width: 250}}
                                >
                                    <select
                                        className="form-select"
                                        style={{
                                            paddingTop: 10,
                                            paddingBottom: 10,
                                            height: '100%',
                                            marginBottom: 10,
                                        }}
                                        aria-label="Select Insignia"
                                        onChange={this.changeCategory}
                                    >
                                        {ins.filter(m => !m.isCAF).length > 0 && ins.length > 3 && <option value={-1}>Original</option>}
                                        {ins.filter(m => m.isCAF).length > 0 && <option value={-2}>All</option>}
                                        {ins.filter(m => m.fpc_id === 21).length > 0 && <option value={21}>Academic</option>}
                                        {ins.filter(m => m.fpc_id === 22).length > 0 && <option value={22}>Honors</option>}
                                        {ins.filter(m => m.fpc_id === 23).length > 0 && <option value={23}>Degrees</option>}
                                        {ins.filter(m => m.fpc_id === 24).length > 0 && !excludeMilitary && <option value={24}>Military</option>}
                                        {ins.filter(m => m.fpc_id === 25).length > 0 && <option value={25}>Profession</option>}
                                        {ins.filter(m => m.fpc_id === 26).length > 0 && <option value={26}>Sports</option>}
                                        {ins.filter(m => m.fpc_id === 27).length > 0 && <option value={27}>Mascots</option>}
                                        {ins.filter(m => m.fpc_id === 29).length > 0 && <option value={29}>Interests</option>}
                                        {ins.filter(m => m.fpc_id === 40).length > 0 && <option value={40}>State Seals</option>}
                                    </select>
                                </div>
                            ) : null;
                        })()}

                        <InsigniaList
                            items={insigniaFilteredList()}
                            collapsed={this.collapsed}
                            collapsedNumber={this.collapsed_number}
                            save={this.save}
                        />

                        {insigniaFilteredList().length > 4 && (
                            <button
                                className="btn btn-outline-secondary btn-sm btn-toggle"
                                onClick={this.collapseToggle}
                                style={{margin: '4px 0 2px 0', color: '#000000', display: 'flex', justifySelf: 'center'}}
                                aria-label={this.collapsed ? `+ View all ${insigniaFilteredList().length} insignia` : '- View less insignia'}
                            >
                                {this.collapsed ? `+ View ${insigniaFilteredList().length - 4} more insignia` : '- View less insignia'}
                            </button>
                        )}

                        {insigniaRemovable() && (
                            <div
                                className="ep-toggle text-center"
                                style={{marginBottom: '1rem'}}
                            >
                                <hr style={{margin: '0.5rem 0'}} />
                                <button
                                    type="button"
                                    aria-label="Remove insignia from product"
                                    className="btn btn-secondary"
                                    onClick={this.ins_remove}
                                >
                                    Remove Insignia
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}
